// SalesReport.tsx

import React, { useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Alert,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme
} from '@mui/material';

import { useReportContext, ReportTabType } from '../../contexts/ReportContext';
import { ReportType, ReportUnit } from '../../types/sales';
import { ReportFilter } from './SalesReportFilter';
import { SupervisorReport } from './Report/SupervisorReport/index';
import { SupervisorCustomerReport } from './Report/SupervisorCustomerReport/index';
import { CompanyTrendReport } from './Report/CompanyTrendReport/index';

// ReportTabTypeとReportTypeが同じ形式（ケバブケース）で定義されている場合、
// マッピングは不要か同一になります
export const SalesReport: React.FC = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  
  // ReportContextを使用
  const {
    activeTab,
    setActiveTab,
    getFilterParams,
    getTabFilterParams,
    shouldDisplayReport,
    reportDisplayed,
    triggerReportDisplay,
    markReportAsDisplayed
  } = useReportContext();

  // 現在アクティブなタブのフィルターパラメータを取得
  const filterParams = getFilterParams();

  // ReportTabTypeとReportTypeが同じ形式なら変換は不要
  const activeReportType: ReportType = activeTab as unknown as ReportType;

  // タブ切り替え時にレポート表示を自動化
  useEffect(() => {
    // レポートが一度でも表示されたことがある場合のみ、タブ切り替え時に自動表示
    if (reportDisplayed && shouldDisplayReport) {
      markReportAsDisplayed(); // 表示フラグをリセット
    }
  }, [activeTab, reportDisplayed, shouldDisplayReport, markReportAsDisplayed]);

  // レポートタイプ切り替え時のタブハンドラー
  const handleTabChange = (event: React.SyntheticEvent, newValue: ReportType) => {
    // 同じ形式なら直接変換可能
    setActiveTab(newValue as unknown as ReportTabType);
    
    // レポートが以前に表示されていた場合は、自動的にデータ取得をトリガー
    if (reportDisplayed) {
      triggerReportDisplay();
    }
  };

  // レポートコンポーネントの条件付きレンダリング
  const renderReportContent = () => {
    if (!reportDisplayed) {
      return (
        <Alert severity="info" sx={{ mt: 3 }}>
          上記の条件を設定し、「レポート表示」ボタンをクリックしてください。
        </Alert>
      );
    }

    // 各レポートタイプに対応するフィルターパラメータを取得
    const tabParams = getTabFilterParams(activeTab);

    switch (activeTab) {
      case 'supervisor':
        return (
          <SupervisorReport 
            startMonth={tabParams.startDate} 
            endMonth={tabParams.endDate} 
            // confirmedOnly={tabParams.isConfirmedOnly} 
          />
        );
      case 'supervisor-customer':
        return (
          <SupervisorCustomerReport 
            startMonth={tabParams.startDate} 
            endMonth={tabParams.endDate} 
            // confirmedOnly={tabParams.isConfirmedOnly} 
          />
        );
      case 'company-trend':
        return (
          <CompanyTrendReport 
            startMonth={tabParams.startDate} 
            endMonth={tabParams.endDate} 
            unit={tabParams.unit || 'monthly'}
            // confirmedOnly={tabParams.isConfirmedOnly} 
          />
        );
      default:
        return null;
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        売上実績レポート
      </Typography>
      
      <Tabs 
        value={activeReportType} 
        onChange={handleTabChange}
        variant={isSmallScreen ? "scrollable" : "fullWidth"}
        scrollButtons={isSmallScreen ? "auto" : false}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="担当者別" value="supervisor" />
        <Tab label="担当者×顧客別" value="supervisor-customer" />
        {/* <Tab label="会社全体トレンド" value="company-trend" /> */}
      </Tabs>
      
      <ReportFilter 
        reportType={activeReportType}
      />
      
      <Box sx={{ mt: 3 }}>
        {renderReportContent()}
      </Box>
    </Paper>
  );
};